import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Card,
  Tooltip,
  Popconfirm,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import {
  PlusOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { CopierSetting, Account } from '../../types';
import { settingsApi } from '../../api/settings';
import { accountsApi } from '../../api/accounts';
import SettingFormModal, { SettingFormValues } from './SettingFormModal';

const { Title } = Typography;

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<CopierSetting[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [modeFilter, setModeFilter] = useState<string | undefined>(undefined);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<CopierSetting | null>(null);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await settingsApi.getAll();
      setSettings(data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '설정 목록을 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await accountsApi.getAll();
      setAccounts(data);
    } catch (error: any) {
      message.error('계좌 목록을 불러오는데 실패했습니다');
    }
  };

  const loadGroups = async () => {
    try {
      const data = await accountsApi.getGroups();
      setGroups(data);
    } catch (error: any) {
      message.error('그룹 목록을 불러오는데 실패했습니다');
    }
  };

  useEffect(() => {
    loadSettings();
    loadAccounts();
    loadGroups();
  }, []);

  const getAccountDisplay = (accountId: string): string => {
    const acc = accounts.find((a) => a.accountId === accountId);
    return acc ? `${accountId} (${acc.name})` : accountId;
  };

  // Targets에 실제 값 표시 (계좌번호 또는 그룹명)
  const getTargetSummary = (setting: CopierSetting): string => {
    if (!setting.targets || setting.targets.length === 0) return '-';
    return setting.targets.map((t) => t.targetValue).join(', ');
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'ACTIVE': return <Tag color="success">활성</Tag>;
      case 'PAUSED': return <Tag color="warning">일시중지</Tag>;
      case 'DELETED': return <Tag color="default">삭제됨</Tag>;
      default: return <Tag>{status}</Tag>;
    }
  };

  const getModeTag = (mode: string) => {
    if (mode === 'EQUITY') return <Tag color="blue">Equity</Tag>;
    if (mode === 'VOLUME') return <Tag color="purple">Volume</Tag>;
    if (mode === 'FIXED') return <Tag color="orange">Fixed</Tag>;
    return <Tag>{mode}</Tag>;
  };

  const filteredData = settings.filter((s) => {
    if (s.status === 'DELETED') return false;
    if (searchText) {
      const lower = searchText.toLowerCase();
      const matchName = s.name.toLowerCase().includes(lower);
      const matchSource = s.sourceAccount.includes(searchText);
      if (!matchName && !matchSource) return false;
    }
    if (statusFilter && s.status !== statusFilter) return false;
    if (modeFilter && s.copyMode !== modeFilter) return false;
    return true;
  });

  const handleAddNew = () => {
    setEditingSetting(null);
    setModalOpen(true);
  };

  const handleEdit = (setting: CopierSetting) => {
    setEditingSetting(setting);
    setModalOpen(true);
  };

  const handlePause = async (id: number) => {
    try {
      await settingsApi.pause(id);
      message.success('설정이 일시중지되었습니다');
      loadSettings();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '일시중지 실패');
    }
  };

  const handleResume = async (id: number) => {
    try {
      await settingsApi.resume(id);
      message.success('설정이 재개되었습니다');
      loadSettings();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '재개 실패');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await settingsApi.delete(id);
      message.success('설정이 삭제되었습니다');
      loadSettings();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '삭제 실패');
    }
  };

  const handleModalSubmit = async (values: SettingFormValues) => {
    try {
      const targets =
        values.targetType === 'ACCOUNT'
          ? values.targetAccounts.map((acc) => ({
              targetType: 'ACCOUNT' as const,
              targetValue: acc,
            }))
          : values.targetGroups.map((grp) => ({
              targetType: 'GROUP' as const,
              targetValue: grp,
            }));

      const payload = {
        name: values.name,
        description: values.description,
        sourceAccount: values.sourceAccount,
        copyMode: values.copyMode,
        copyValue: values.copyValue,
        status: values.status,
        targets,
      };

      if (editingSetting) {
        await settingsApi.update(editingSetting.id, payload);
        message.success('설정이 수정되었습니다');
      } else {
        await settingsApi.create(payload);
        message.success('설정이 추가되었습니다');
      }

      setModalOpen(false);
      setEditingSetting(null);
      loadSettings();
    } catch (error: any) {
      const msg = error?.response?.data?.message || '저장 실패';
      message.error(msg);
    }
  };

  const columns: TableColumnsType<CopierSetting> = [
    { title: 'ID', dataIndex: 'id', width: 60, sorter: (a, b) => a.id - b.id },
    {
      title: '이름',
      dataIndex: 'name',
      width: 200,
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Tooltip title={record.description || '설명 없음'}>
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'sourceAccount',
      width: 240,
      render: (val) => <span style={{ fontFamily: 'monospace' }}>{getAccountDisplay(val)}</span>,
    },
    {
      title: '모드',
      dataIndex: 'copyMode',
      width: 100,
      render: getModeTag,
    },
    {
      title: 'Value',
      dataIndex: 'copyValue',
      width: 90,
      align: 'right',
      sorter: (a, b) => a.copyValue - b.copyValue,
      render: (val, record) => record.copyMode === 'FIXED' ? `${val} Lots` : `${val}%`,
    },
    {
      title: 'Targets',
      width: 280,
      ellipsis: true,
      render: (_, record) => (
        <Tooltip title={getTargetSummary(record)}>
          <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
            {getTargetSummary(record)}
          </span>
        </Tooltip>
      ),
    },
    { title: '상태', dataIndex: 'status', width: 100, render: getStatusTag },
    {
      title: '동작',
      width: 160,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          {record.status === 'ACTIVE' ? (
            <Tooltip title="일시중지">
              <Button size="small" icon={<PauseCircleOutlined />} onClick={() => handlePause(record.id)} />
            </Tooltip>
          ) : (
            <Tooltip title="재개">
              <Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={() => handleResume(record.id)} />
            </Tooltip>
          )}
          <Tooltip title="수정">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="설정을 삭제하시겠습니까?"
            description="복사 이력은 유지되지만 설정은 비활성화됩니다."
            okText="삭제"
            cancelText="취소"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="삭제">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleResetFilters = () => {
    setSearchText('');
    setStatusFilter(undefined);
    setModeFilter(undefined);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Copier 설정</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { loadSettings(); loadAccounts(); loadGroups(); }} loading={loading}>새로고침</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>설정 추가</Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <Input
            placeholder="이름 또는 Source 계좌 검색"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
          <Select
            placeholder="상태"
            value={statusFilter}
            onChange={setStatusFilter}
            allowClear
            style={{ width: 140 }}
            options={[
              { value: 'ACTIVE', label: '활성' },
              { value: 'PAUSED', label: '일시중지' },
            ]}
          />
          <Select
            placeholder="모드"
            value={modeFilter}
            onChange={setModeFilter}
            allowClear
            style={{ width: 140 }}
            options={[
              { value: 'EQUITY', label: 'Equity' },
              { value: 'VOLUME', label: 'Volume' },
              { value: 'FIXED', label: 'Fixed' },
            ]}
          />
          <Button onClick={handleResetFilters}>초기화</Button>
          <span style={{ color: '#999' }}>
            총 {filteredData.length}개 표시 (전체 {settings.filter((s) => s.status !== 'DELETED').length}개) | 계좌 {accounts.length}개 / 그룹 {groups.length}개
          </span>
        </Space>
      </Card>

      <Table<CopierSetting>
        rowKey="id"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `총 ${total}건` }}
        scroll={{ x: 1280 }}
      />

      <SettingFormModal
        open={modalOpen}
        initialData={editingSetting}
        accounts={accounts}
        groups={groups}
        allSettings={settings}
        onCancel={() => {
          setModalOpen(false);
          setEditingSetting(null);
        }}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
};

export default Settings;
